import React, { useState, useEffect } from "react";
import { ReactComponent as OutlinePlus16 } from "assets/svg/outline-plus-16.svg";
import { Button, ListGroup } from "react-bootstrap";
import { Link } from "react-router-dom";
import querySchemas from "services/querySchemas";
import ShareDocsModal from "../../patient-charts/components/patient-chart/components/portal/ShareDocsModal";
import Checkbox from "components/checkbox/Checkbox";
import ErrorMessage from "components/error-message";
import ToggleSwitch from "components/toggle-switch";
import styles from "./ClientCommunication.module.scss";
import postOrchidPro from "services/postOrchidPro";
import setIntakeForms from "services/patient-chart/setIntakeForms";
import listIntakeForms from "services/patient-chart/listIntakeForms";
import { useOrchidPro } from "components/Dashboard/DoctorDashboard/contexts/orchid-pro-context";
import { AmplitudePageName, track } from "utils/amplitude";
import { useTeamContext } from "contexts/group-practice-context/TeamContext";

interface Props {
  amplitudePageName: AmplitudePageName;
}

const ClientCommunication: React.FC<Props> = ({ amplitudePageName }) => {
  const { orchidPro } = useOrchidPro();
  // welcome email
  const [enable, setEnable] = useState<boolean | undefined>(undefined);

  const [share, setShare] = useState<boolean | undefined>(undefined);
  const [requireCreditCard, setRequireCreditCard] = useState<
    boolean | undefined
  >(undefined);
  const [showShareDocs, setShowShareDocs] = useState(false);
  const [error, setError] = useState<any>();
  const [changed, setChanged] = useState(false);

  const [availableForms, setAvailableForms] = useState([] as any);
  const [selectedForDisplay, setSelectedForDisplay] = useState([] as any);
  const [selectedForSubmit, setSelectedForSubmit] = useState([] as any);

  const { practiceOwnerOrchidUserId } = useTeamContext();

  useEffect(() => {
    async function initData() {
      const availableForms = await querySchemas(
        "form",
        practiceOwnerOrchidUserId,
      );
      setAvailableForms(
        availableForms.filter((el) => el.form_type === "custom"),
      );
      const { general_intake_forms } = await listIntakeForms(
        practiceOwnerOrchidUserId,
      );
      setSelectedForSubmit(general_intake_forms);
      setSelectedForDisplay(general_intake_forms);
    }
    initData().catch(setError);
    // eslint-disable-next-line
  }, [practiceOwnerOrchidUserId]);

  useEffect(() => {
    setShare(orchidPro?.send_intake_forms_when_patient_added);
    setEnable(orchidPro?.send_welcome_email_to_new_patients);
    setRequireCreditCard(orchidPro?.request_credit_card_when_patient_added);
  }, [orchidPro]);

  const addFormsToIntakePackages = async (selectedFromModal) => {
    setSelectedForDisplay((selectedForDisplay) => [
      ...new Set([...selectedForDisplay, ...selectedFromModal]),
    ]);
    setSelectedForSubmit((selectedForSubmit) => [
      ...new Set([...selectedForSubmit, ...selectedFromModal]),
    ]);
    setShowShareDocs(false);
  };

  const saveClientCommunicationSettings = async () => {
    setChanged(true);
    try {
      await postOrchidPro(
        {
          send_intake_forms_when_patient_added: share,
          send_welcome_email_to_new_patients: enable,
          request_credit_card_when_patient_added: requireCreditCard,
        },
        practiceOwnerOrchidUserId,
      );
      await setIntakeForms(selectedForSubmit, practiceOwnerOrchidUserId);
    } catch (error) {
      setError(error);
    } finally {
      setChanged(false);
    }
  };

  const onCheckboxChange = (form) => (event) => {
    setChanged(true);
    const { checked } = event.target;
    let nextSelectedForSubmit = selectedForSubmit;
    if (checked) {
      nextSelectedForSubmit = [...selectedForSubmit, form.form_schema_id];
    } else {
      nextSelectedForSubmit = selectedForSubmit.filter(
        (el) => el !== form.form_schema_id,
      );
    }
    setSelectedForSubmit(nextSelectedForSubmit);
  };

  return (
    <>
      {error && (
        <ErrorMessage
          show={true}
          onHide={() => setError(undefined)}
          errorMsg={error?.toString()}
          className={styles["client-portal-setting__error-message"]}
        />
      )}
      <div className={styles["client-portal-setting__body"]}>
        <div className={styles["client-portal-setting__section"]}>
          <div className={styles["client-portal-setting__section-title"]}>
            Welcome Email
          </div>
          <div className={styles["client-communicate__content"]}>
            Orchid auto-sends a warm welcome to all new patients as soon as you
            create their profile or confirm their first session via your Client
            Portal Link. If you prefer to personalize this process or don't have
            the client's email immediately available, don't worry - we've got
            you covered. To manually send the Welcome Email, simply go to:{" "}
            <b>
              {
                "Patient Chart > [Client Name] > Client Portal > Access to Client Portal"
              }
            </b>
            .
            <br />
            <br />
            From there you can control when and how your clients are welcomed,
            turning the first impression into a lasting connection.
          </div>

          <div className={"d-flex mt-4 align-items-center"}>
            <div className={"rg-14 mr-4"}>
              Auto-send Welcome email to new patients?
            </div>
            <span className={"mr-3"}>
              <b>No</b>
            </span>
            <ToggleSwitch
              checked={enable || false}
              onChange={(e) => {
                setChanged(true);
                setEnable(!enable);
              }}
              onClick={() =>
                track(
                  amplitudePageName,
                  "auto-send-welcome-to-new-patients-toggle",
                  "click",
                )
              }
            />
            <span className={"ml-3"}>
              <b>Yes</b>
            </span>
          </div>
        </div>

        <div className={styles["client-portal-setting__section"]}>
          <div className={styles["client-portal-setting__section-title"]}>
            Manage your Client Intake packages
          </div>
          <div className={styles["client-communicate__content"]}>
            Your clients will receive their personalized Client Intake Package
            as soon as you send the Welcome Email. This comprehensive kit--which
            may include consent forms and assessments--is easily accessible via
            their Client Portal, offering them a seamless journey right from the
            start.
          </div>
          <div className={"bd-16 my-4"}>
            Do you want to share Intake Package to clients?
          </div>
          <div className={"d-flex align-items-center"}>
            <span className={"mr-3"}>No</span>
            <span className={styles["client-communicate__toggle-switch"]}>
              <ToggleSwitch
                checked={share || false}
                onClick={() =>
                  track(
                    amplitudePageName,
                    "share-intake-package-to-clients-toggle",
                    "click",
                  )
                }
                onChange={(e) => {
                  setChanged(true);
                  setShare(!share);
                }}
              />
            </span>
            <span className={"ml-3"}>Yes</span>
          </div>
          <div className={"bd-16 my-4"}>
            Do you want to require client's credit card information?
          </div>
          <div className={"d-flex align-items-center"}>
            <span className={"mr-3"}>No</span>
            <span className={styles["client-communicate__toggle-switch"]}>
              <ToggleSwitch
                checked={requireCreditCard || false}
                onClick={() =>
                  track(
                    amplitudePageName,
                    "require-credit-card-information-toggle",
                    "click",
                  )
                }
                onChange={(e) => {
                  setChanged(true);
                  setRequireCreditCard(!requireCreditCard);
                }}
              />
            </span>
            <span className={"ml-3"}>Yes</span>
          </div>
          <hr />
          <div className={"rg-14"}>
            Add documents you want to include in the client intake packages.
          </div>
          {selectedForDisplay.length > 0 && (
            <ListGroup className={styles["client-communicate__list-group"]}>
              {availableForms
                .filter((form) =>
                  selectedForDisplay.includes(form.form_schema_id),
                )
                .map((form) => {
                  return (
                    <ListGroup.Item
                      className={styles["client-communicate__list-item"]}
                      key={form.form_schema_id}
                    >
                      <Checkbox
                        variant={"rectangle"}
                        onChange={onCheckboxChange(form)}
                        checked={
                          selectedForSubmit.includes(form.form_schema_id) ||
                          false
                        }
                        onClick={() =>
                          track(
                            amplitudePageName,
                            "client-intake-package-document-checkbox",
                            "click",
                          )
                        }
                      />
                      {form.title}
                    </ListGroup.Item>
                  );
                })}
            </ListGroup>
          )}

          <Button
            variant={"link"}
            className={"p-0 d-flex align-items-center mt-4"}
            onClick={() => {
              setChanged(true);
              setShowShareDocs(true);
              track(amplitudePageName, "add-documents-button", "click");
            }}
          >
            <OutlinePlus16 className={"mr-2"} /> Add documents
          </Button>
        </div>
        <div className={styles["client-portal-setting__section"]}>
          <div className={styles["client-portal-setting__section-title"]}>
            Document Templates
          </div>
          <div className={styles["client-communicate__content"]}>
            Craft, attach, and share essential documents with your clients via
            the Client Portal, allowing them to complete or sign necessary forms
            at their convenience. Leveraging Orchid, you can monitor the
            completion status of these documents effortlessly.
            <br />
            <br />
            To create a new document template, simply navigate to{" "}
            <Link
              onClick={() =>
                track(amplitudePageName, "notes-&-forms-link", "click")
              }
              to={"/pro-dashboard/notes-and-forms"}
            >
              Template Library
            </Link>
            . This is where you can edit & create your documentation to fit your
            unique practice needs, enhancing the client's journey while
            maintaining professional standards.
          </div>
        </div>
        <ShareDocsModal
          show={showShareDocs}
          onHide={() => {
            setShowShareDocs(false);
            setError(undefined);
          }}
          onConfirm={addFormsToIntakePackages}
          error={error}
          formPackages={true}
        />
      </div>
      <div className={styles["client-portal-setting__footer"]}>
        <div className={"gray01 rg-12"}>
          ©2026 Orchid Exchange Inc. All rights reserved
        </div>
        <Button
          variant="primary"
          onClick={() => {
            track(
              amplitudePageName,
              "save-shared-docs-settings-button",
              "click",
            );
            saveClientCommunicationSettings();
          }}
          className={styles["client-portal-setting__save-button"]}
          disabled={!changed}
        >
          Save
        </Button>
      </div>
    </>
  );
};

export default ClientCommunication;
